// StoryboardViewer.tsx — wide. Ten steps across, then another row.
//
// Erez, 15 Aug, fourth attempt: "I want to see it to the width — like 10 steps
// and then another row, not just one column."
//
// The three before this each failed for a different reason and each taught
// something. A flat sheet with captions baked into the JPEG is a picture of
// text. A card gallery is a contact sheet — right for checking coverage, wrong
// for judging a story. One-step-per-row was the right idea in the wrong shape:
// it read as a sequence, but it wasted the whole width of the board and turned
// twenty-four shots into a scroll you never reach the end of.
//
// So: ten steps per row, wrapping into as many rows as the episode needs, the
// whole episode visible at once. Every word is still real text, typeset small.
// And clicking a step opens it full width with the big frame and the narration
// at reading size — the walk-down view is not gone, it is one click away.
import { useState } from "react";
import { STORYBOARDS, FRAMES, type SBEpisode, type SBShot } from "./storyboards.data";

const FONT = "Inter, system-ui, -apple-system, sans-serif";
const SERIF = "'Playfair Display', Georgia, 'Times New Roman', serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const INK = "#1C1917", INK2 = "#57534E", MUTED = "#8A8177", LINE = "#E7DED3";
const PETROL = "#2B5560", SAGE = "#4F7A4F", PAPER = "#FFFFFF";

const COLS = 10;                    // ten steps, then another row — his number
const CELL_W = 330;                 // a frame you can read a face in, x10 across
const GAP = 22;
const GRID_W = COLS * CELL_W + (COLS - 1) * GAP;   // 3498
const OPEN_W = 1180;                // the expanded read, same size as before

const runtime = (ep: SBEpisode) => ep.shots.reduce((n, s) => n + s.sec, 0);
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/* ── one step, small: the frame, then the words underneath at scanning size ── */
function Cell({ s, n, total, onOpen }: { s: SBShot; n: number; total: number; onOpen: () => void }) {
  return (
    <div
      onClick={onOpen}
      style={{ width: CELL_W, cursor: "zoom-in" }}
      title="Click to open this shot full width"
    >
      <div style={{ position: "relative" }}>
        <img
          src={`${FRAMES}/${s.img}`}
          alt={s.action}
          decoding="async"
          style={{
            width: CELL_W, height: Math.round(CELL_W * 9 / 16), objectFit: "cover",
            display: "block", borderRadius: 10, background: "#DED7CF",
            boxShadow: "0 8px 20px rgba(0,0,0,0.11)",
          }}
        />
        {/* step number sits ON the frame so the grid still reads as a sequence */}
        <div style={{
          position: "absolute", top: 8, left: 8, minWidth: 30, height: 30, padding: "0 8px",
          borderRadius: 15, background: "rgba(28,25,23,0.86)", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: MONO, fontSize: 13, fontWeight: 700,
        }}>{s.id}</div>
        {s.text ? (
          <div style={{
            position: "absolute", bottom: 8, left: 8, right: 8,
            fontFamily: MONO, fontSize: 10.5, color: "#fff", letterSpacing: "0.04em",
            background: "rgba(79,122,79,0.92)", borderRadius: 5, padding: "3px 7px",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>ON SCREEN — {s.text}</div>
        ) : null}
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "10px 0 7px" }}>
        <span style={{ fontFamily: MONO, fontSize: 10.5, color: PETROL, letterSpacing: "0.07em" }}>
          {s.size} · {s.move.toUpperCase()}
        </span>
        <span style={{ fontFamily: MONO, fontSize: 10.5, color: MUTED }}>{s.sec}s</span>
        <span style={{ fontFamily: MONO, fontSize: 10.5, color: MUTED, marginLeft: "auto" }}>{n}/{total}</span>
      </div>

      {/* the narration, whole — small, but real text, not a picture of it */}
      <div style={{ fontFamily: SERIF, fontSize: 15, lineHeight: 1.5, color: INK }}>
        {s.vo || <span style={{ color: MUTED, fontStyle: "italic" }}>picture only — no narration</span>}
      </div>

      <div style={{ fontFamily: FONT, fontSize: 11.5, color: INK2, lineHeight: 1.45, marginTop: 8 }}>
        {s.action}
      </div>
    </div>
  );
}

/* ── the same step, opened: spans the full grid, big frame, reading-size type ── */
function OpenStep({ s, n, total, onClose }: { s: SBShot; n: number; total: number; onClose: () => void }) {
  return (
    <div style={{
      gridColumn: "1 / -1", display: "flex", gap: 34, alignItems: "flex-start",
      background: "#FBF8F4", border: `1px solid ${LINE}`, borderRadius: 18, padding: 30,
      // it takes the whole row, but it does not have to be as wide as the row —
      // a text column 3000px across is unreadable, so cap it at a sane measure
      maxWidth: OPEN_W + 900, justifySelf: "start",
    }}>
      <div style={{ flexShrink: 0 }}>
        <img
          src={`${FRAMES}/${s.img}`}
          alt={s.action}
          decoding="async"
          style={{
            width: OPEN_W, height: Math.round(OPEN_W * 9 / 16), objectFit: "cover",
            display: "block", borderRadius: 14, background: "#DED7CF",
            boxShadow: "0 18px 44px rgba(0,0,0,0.13)",
          }}
        />
      </div>

      <div style={{ flex: 1, minWidth: 420 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
          <div style={{
            minWidth: 52, height: 52, padding: "0 12px", borderRadius: 26, background: INK, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: MONO, fontSize: 19, fontWeight: 700,
          }}>{s.id}</div>
          <span style={{ fontFamily: MONO, fontSize: 12.5, color: MUTED }}>STEP {n} OF {total}</span>
          <button
            onClick={onClose}
            style={{
              marginLeft: "auto", cursor: "zoom-out", border: `1px solid ${LINE}`, background: PAPER,
              color: INK2, borderRadius: 9, padding: "8px 14px", fontFamily: FONT, fontSize: 13,
            }}
          >Close</button>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
          <span style={{ fontFamily: MONO, fontSize: 12.5, color: PETROL, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {s.size} · {s.angle} · {s.move}
          </span>
          <span style={{ fontFamily: MONO, fontSize: 12.5, color: MUTED }}>{s.sec} SECONDS</span>
          {s.text ? (
            <span style={{
              fontFamily: MONO, fontSize: 12, color: SAGE, border: `1px solid ${SAGE}33`,
              background: "#F1F7F1", borderRadius: 6, padding: "3px 9px", letterSpacing: "0.04em",
            }}>ON SCREEN — {s.text}</span>
          ) : null}
        </div>

        <div style={{ fontFamily: SERIF, fontSize: 27, lineHeight: 1.45, color: INK, letterSpacing: "-0.005em" }}>
          {s.vo || <span style={{ color: MUTED, fontStyle: "italic" }}>picture only — no narration</span>}
        </div>

        <div style={{ fontFamily: FONT, fontSize: 14.5, color: INK2, lineHeight: 1.5, marginTop: 16 }}>
          <span style={{ color: MUTED, fontFamily: MONO, fontSize: 12, marginRight: 8 }}>NOTE</span>
          {s.action}
        </div>
      </div>
    </div>
  );
}

/* ── one episode: its masthead, then its grid. No tab, no hiding. ── */
function Episode({ ep, n, open, setOpen }: {
  ep: SBEpisode; n: number;
  open: string | null; setOpen: (id: string | null) => void;
}) {
  return (
    <div>
      <div style={{ marginBottom: 26, paddingBottom: 18, borderBottom: `2px solid ${INK}` }}>
        <div style={{ fontFamily: MONO, fontSize: 12.5, color: PETROL, letterSpacing: "0.14em", marginBottom: 10 }}>
          EPISODE {n} · HOW THE WORLD WORKS · SCRIPT, NOT YET FILMED
        </div>
        <div style={{ fontFamily: SERIF, fontSize: 44, lineHeight: 1.15, color: INK, letterSpacing: "-0.02em" }}>
          {ep.title.includes(" — ") ? ep.title.split(" — ")[1] : ep.title}
        </div>
        <div style={{ display: "flex", gap: 22, marginTop: 12, fontFamily: MONO, fontSize: 13, color: INK2 }}>
          <span>{(ep.title.split(" · ")[1]?.split(" — ")[0] ?? "").toUpperCase()}</span>
          <span>{ep.shots.length} SHOTS</span>
          <span>{mmss(runtime(ep))} RUNTIME</span>
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: `repeat(${COLS}, ${CELL_W}px)`,
        columnGap: GAP, rowGap: 36, alignItems: "start",
      }}>
        {ep.shots.map((s, i) => {
          const key = `${ep.slug}:${s.id}`;
          return open === key ? (
            <OpenStep key={key} s={s} n={i + 1} total={ep.shots.length} onClose={() => setOpen(null)} />
          ) : (
            <Cell key={key} s={s} n={i + 1} total={ep.shots.length} onOpen={() => setOpen(key)} />
          );
        })}
      </div>
    </div>
  );
}

export function StoryboardViewer() {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="nopan nodrag" style={{ pointerEvents: "auto", fontFamily: FONT, width: GRID_W }}>
      {/* Erez, 15 Aug: "don't put it in like a thing so I can see the whole thing."
          So no tabs — every episode is a section, one under the other, all present. */}
      <div style={{ marginBottom: 34 }}>
        <div style={{ fontFamily: SERIF, fontSize: 34, color: INK, letterSpacing: "-0.015em" }}>
          Four episodes, written and storyboarded
        </div>
        <div style={{ fontFamily: FONT, fontSize: 14.5, color: INK2, marginTop: 8, maxWidth: 1100, lineHeight: 1.55 }}>
          All four are below, one under the other. Ten shots per row. Click any shot to open it at full size
          with its narration at reading size.
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 78 }}>
        {STORYBOARDS.map((ep, i) => (
          <Episode key={ep.slug} ep={ep} n={i + 1} open={open} setOpen={setOpen} />
        ))}
      </div>

      <div style={{ marginTop: 44, paddingTop: 20, borderTop: `1px solid ${LINE}`, fontFamily: FONT, fontSize: 13, color: MUTED, maxWidth: 1100, lineHeight: 1.55 }}>
        Frames are indicative stills chosen to show framing and intent, not the final footage. The line in serif is the
        finished narration — that is the part worth arguing with. Photos via Pexels.
      </div>
    </div>
  );
}
